import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut, signInWithCustomToken } from 'firebase/auth';
import { auth } from './firebase';
import Login from './Login';
import Dashboard from './Dashboard';
import SchoolStudentCategory from './SchoolStudentCategory';
import SetupCategory from './SetupCategory';
import FinancialCategory from './FinancialCategory';
import ReportsCategory from './ReportsCategory';
import MarketingOrderForm from './MarketingOrderForm';
import OrderHistory from './OrderHistory';
import AddSchool from './AddSchool';
import AddStudent from './AddStudent';
import ViewStudents from './ViewStudents';
import ViewSchools from './ViewSchools';
import AddBulkFees from './AddBulkFees';  
import QuickPayment from './QuickPayment';
import OutstandingFeesReport from './OutstandingFeesReport';
import IncomeFeesReport from './IncomeFeesReport';
import OutstandingAdminFeesReport from './OutstandingAdminFeesReport';
import TermStructureSetup from './TermStructureSetup';
import HolidayManagement from './HolidayManagement';
import './App.css';
import './CategoryPage.css'; 
import LessonCalendar from './LessonCalendar';
import AddProspectSchool from './AddProspectSchool';  
import PendingEnrollments from './PendingEnrollments';
import FranchiseeInformation from './FranchiseeInformation';
import StatementGeneration from './StatementGeneration';
import MonthEndProcessing from './MonthEndProcessing';
import ViewMonthEndSubmissions from './ViewMonthEndSubmissions';
import EmailLogReport from './EmailLogReport';
import TShirtRecon from './TShirtRecon';
import SchoolCommissionReport from './SchoolCommissionReport';


function App() {
  // ALL HOOKS MUST COME FIRST (before any returns or conditionals)
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState('dashboard');
  const [navigationHistory, setNavigationHistory] = useState(['dashboard']);
  const [editOrderId, setEditOrderId] = useState(null);
  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [returnToView, setReturnToView] = useState(null);
  const [editSchoolData, setEditSchoolData] = useState(null);
  const [viewStudentsFilters, setViewStudentsFilters] = useState({
    school: 'all',
    search: '',
    paymentStatus: 'all'
  });
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [impersonating, setImpersonating] = useState(null); // { name, uid }

  

  // Navigation helper that tracks history
  const navigateTo = (view) => {
    setNavigationHistory(prev => [...prev, view]);
    setCurrentView(view);
  };

  // Go back to previous view
  const goBack = () => {
    if (navigationHistory.length > 1) {
      const newHistory = [...navigationHistory];
      newHistory.pop();
      const previousView = newHistory[newHistory.length - 1];
      setNavigationHistory(newHistory);
      setCurrentView(previousView);
    } else {
      setCurrentView('dashboard');
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
  const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
    setUser(currentUser);
    setLoading(false);
  });

  return () => unsubscribe();
}, []);




  // Handle impersonation token from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const name  = params.get('name');
    if (token) {
      // Clear token from URL immediately
      window.history.replaceState({}, '', '/');
      signInWithCustomToken(auth, token)
        .then(() => {
          setImpersonating({ name: decodeURIComponent(name || 'Franchisee') });
        })
        .catch(err => {
          console.error('Impersonation failed:', err);
          alert('Impersonation failed: ' + err.message);
        });
    }
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setCurrentView('dashboard');
      setEditOrderId(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavigate = (view) => {
    navigateTo(view);
    if (view !== 'quickPayment') {
      setSelectedStudentId(null);
      setReturnToView(null);
    }
  };

  const handleApproveEnrollment = (studentData) => {
    setEnrollmentData(studentData);
    navigateTo('addStudent');
  };

  const handleStudentAdded = () => {
    setCurrentView('schoolStudentCategory');
    setEnrollmentData(null);
  };

  const handleEditOrder = (orderId) => {
    setEditOrderId(orderId);
    setCurrentView('orderForm');
  };

  const handleOrderSubmitted = () => {
    setEditOrderId(null);
    setCurrentView('dashboard');
  };


  const handleExitImpersonation = async () => {
    await signOut(auth);
    setImpersonating(null);
    window.location.href = '/admin';
  };

  const handleSchoolAdded = () => {
    setCurrentView('dashboard');
  };

  const handleStudentsQuickAdded = () => {
    setCurrentView('dashboard');
  };

  const handleHolidayManagementClose = () => {
    setCurrentView('dashboard');
  };

  


  // Impersonation banner — shown at top of every page when impersonating
  const ImpersonationBanner = () => impersonating ? (
    <div style={{
      background: '#c62828', color: '#fff', padding: '10px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      fontSize: '14px', fontWeight: 600, zIndex: 99999,
      position: 'sticky', top: 0
    }}>
      <span>
        ⚠️ ADMIN VIEW — You are logged in as <strong>{impersonating.name}</strong>.
        Changes you make here are REAL.
      </span>
      <button
        onClick={handleExitImpersonation}
        style={{
          background: '#fff', color: '#c62828', border: 'none',
          borderRadius: '6px', padding: '6px 14px', fontWeight: 700,
          cursor: 'pointer', fontSize: '13px'
        }}
      >
        ✕ Exit — Back to Admin Dashboard
      </button>
    </div>
  ) : null;

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px', fontSize: '18px' }}>Loading...</div>;
  }
  

  if (!user) {
    return <Login onLoginSuccess={() => setUser(auth.currentUser)} />;
  }

  // Inject banner into every page render
  const withBanner = (component) => (
    <><ImpersonationBanner />{component}</>
  );

  // Clean Navigation Bar (for non-dashboard views)
  const NavBar = () => (
    <div style={{
      background: 'linear-gradient(135deg, #2B4BA0 0%, #3B5FC0 100%)',
      padding: '24px',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      gap: '15px'
    }}>
      <button
        onClick={() => {
          setNavigationHistory(['dashboard']);
          setCurrentView('dashboard');
          setViewStudentsFilters({ school: 'all', search: '', paymentStatus: 'all' });
        }}
        style={{
          background: 'linear-gradient(135deg, #2B4BA0 0%, #1E3A8A 100%)',
          color: 'white',
          border: '2px solid white',
          padding: '12px 24px',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer'
        }}
      >
        ← Back to Dashboard
      </button>
      
      {currentView !== 'dashboard' && (
        <button
          onClick={goBack}
          style={{
            background: 'linear-gradient(135deg, #FFF200 0%, #FFE500 100%)',
            color: '#2B4BA0',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          title="Go back to previous page"
        >
          ✕
        </button>
      )}
    </div>
  );

  // Category Pages
  if (currentView === 'schoolStudentCategory') {
    return (
      <SchoolStudentCategory
        onNavigate={handleNavigate}
        onClose={() => setCurrentView('dashboard')}
      />
    );
  }

  if (currentView === 'setupCategory') {
    return (
      <SetupCategory
        onNavigate={handleNavigate}
        onClose={() => setCurrentView('dashboard')}
      />
    );
  }

  if (currentView === 'financialCategory') {
    return (
      <FinancialCategory
        onNavigate={handleNavigate}
        onClose={() => setCurrentView('dashboard')}
      />
    );
  }

  if (currentView === 'reportsCategory') {
    return (
      <ReportsCategory
        onNavigate={handleNavigate}
        onClose={() => setCurrentView('dashboard')}
      />
    );
  }

  // Month-End Processing View
  if (currentView === 'monthEndProcessing') {
    return (
      <div>
        <NavBar />
        <MonthEndProcessing 
          onClose={() => setCurrentView('financialCategory')}
        />
      </div>
    );
  }

  // View Month-End Submissions
  if (currentView === 'viewMonthEndSubmissions') {
    return (
      <div>
        <NavBar />
        <ViewMonthEndSubmissions 
          onClose={() => setCurrentView('financialCategory')}
        />
      </div>
    );
  }

  // Outstanding Fees Report View
  if (currentView === 'outstandingFees') {
    return (
      <div>
        <NavBar />
        <OutstandingFeesReport 
          onClose={() => setCurrentView('dashboard')}
          onRecordPayment={(studentId) => {
            setSelectedStudentId(studentId);
            setReturnToView('outstandingFees');
            setCurrentView('quickPayment');
          }}
        />
      </div>
    );
  }

  // Income Fees Report View
  if (currentView === 'incomeFeesReport') {
    return (
      <div>
        <NavBar />
        <IncomeFeesReport 
          onClose={() => setCurrentView('dashboard')}
        />
      </div>
    );
  }

  // Outstanding Admin Fees Report View
  if (currentView === 'outstandingAdminFees') {
    return (
      <div>
        <NavBar />
        <OutstandingAdminFeesReport 
          onClose={() => setCurrentView('reportsCategory')}
        />
      </div>
    );
  }

  // Email Log Report View
if (currentView === 'emailLog') {
  return (
    <div>
      <NavBar />
      <EmailLogReport
        onClose={() => setCurrentView('reportsCategory')}
      />
    </div>
  );
}

  // Term Structure Setup View
  if (currentView === 'termStructureSetup') {
    return (
      <div>
        <NavBar />
        <TermStructureSetup 
          onClose={() => setCurrentView('dashboard')}
        />
      </div>
    );
  }

  // Holiday Management View
  if (currentView === 'holidayManagement') {
    return (
      <div>
        <NavBar />
        <HolidayManagement 
          onClose={() => setCurrentView('dashboard')}
        />
      </div>
    );
  }

  if (currentView === 'lessonCalendar') {
    return (
      <div>
        <NavBar />
        <LessonCalendar 
          onClose={() => setCurrentView('setupCategory')}
          onActivateSchool={(prospectData) => {
            setEditSchoolData(prospectData);
            setCurrentView('editSchool');
          }}
        />
      </div>
    );
  }

  if (currentView === 'addProspect') {
    return (
      <div>
        <NavBar />
        <AddProspectSchool 
          onClose={() => setCurrentView('dashboard')}
        />
      </div>
    );
  }

  // View Students List
  if (currentView === 'viewStudents') {
    return (
      <div>
        <NavBar />
        <ViewStudents 
          onEditStudent={(studentId) => {
            alert('Edit student feature coming soon!');
          }}
          onRecordPayment={(studentId) => {
            setSelectedStudentId(studentId);
            setReturnToView('viewStudents');
            setCurrentView('quickPayment');
          }}
          onClose={() => setCurrentView('schoolStudentCategory')}
          initialFilters={viewStudentsFilters}
        />
      </div>
    );
  }

  // View Schools List
  if (currentView === 'viewSchools') {
    return (
      <div>
        <NavBar />
        <ViewSchools 
          onEditSchool={(schoolData) => {
            setEditSchoolData(schoolData);
            setCurrentView('editSchool');
          }}
          onClose={() => setCurrentView('schoolStudentCategory')}
        />
      </div>
    );
  }

  // Edit School
  if (currentView === 'editSchool' && editSchoolData) {
    return (
      <div>
        <NavBar />
        <AddSchool 
          schoolData={editSchoolData}
          isEditMode={true}
          onSchoolAdded={() => {
            setEditSchoolData(null);
            setCurrentView('viewSchools');
          }}
          onCancel={() => {
            setEditSchoolData(null);
            setCurrentView('viewSchools');
          }}
        />
      </div>
    );
  }

  // Add Student View
  if (currentView === 'addStudent') {
    return (
      <div>
        <NavBar />
        <AddStudent 
          onStudentAdded={handleStudentAdded}
          onCancel={() => setCurrentView('dashboard')}
          enrollmentData={enrollmentData}
        />
      </div>
    );
  }

  // Quick Payment View
  if (currentView === 'quickPayment') {
    return (
      <div>
        <NavBar />
        <QuickPayment 
          onClose={() => {
            setCurrentView('dashboard');
            setSelectedStudentId(null);
            setReturnToView(null);
          }}
          preSelectedStudentId={selectedStudentId}
          returnToView={returnToView}
          onReturnToSource={() => {
            if (returnToView) {
              setCurrentView(returnToView);
              setReturnToView(null);
              setSelectedStudentId(null);
            } else {
              setCurrentView('dashboard');
              setSelectedStudentId(null);
            }
          }}
        />
      </div>
    );
  }

  // Add Bulk Fees View
  if (currentView === 'addBulkFees') {
    return (
      <div>
        <NavBar />
        <AddBulkFees 
          onClose={() => setCurrentView('dashboard')}
        />
      </div>
    );
  }

  // Add School View
  if (currentView === 'addSchool') {
    return (
      <div>
        <NavBar />
        <AddSchool 
          onSchoolAdded={handleSchoolAdded}
          onCancel={() => setCurrentView('dashboard')}
        />
      </div>
    );
  }



  // Pending Enrollments View
  if (currentView === 'pendingEnrollments') {
    return (
      <div>
        <NavBar />
        <PendingEnrollments
          onNavigate={handleNavigate}
          onApproveEnrollment={handleApproveEnrollment}
        />
      </div>
    );
  }

   // Franchisee Information View
  if (currentView === 'franchiseeInformation') {
    return (
      <div>
        <NavBar />
        <FranchiseeInformation
          onClose={() => setCurrentView('setupCategory')}
        />
      </div>
    );
  }

   // Statement Generation View
  if (currentView === 'statementGeneration') {
    return (
      <div>
        <NavBar />
        <StatementGeneration
          onClose={() => setCurrentView('financialCategory')}
        />
      </div>
    );
  }

  // Order Form View
  if (currentView === 'orderForm') {
    return (
      <div>
        <NavBar />
        <MarketingOrderForm 
          onOrderSubmitted={handleOrderSubmitted} 
          editOrderId={editOrderId}
        />
      </div>
    );
  }

  // Order History View
  if (currentView === 'history') {
    return (
      <div>
        <NavBar />
        <OrderHistory 
          onEditOrder={handleEditOrder}
          onClose={() => setCurrentView('dashboard')}
        />
      </div>
    );
  }

    // T-Shirt Reconciliation Report
  if (currentView === 'tshirtRecon') {
    return (
      <div>
        <NavBar />
        <TShirtRecon
          onClose={() => setCurrentView('reportsCategory')}
        />
      </div>
    );
  }

  // School Commission Report
  if (currentView === 'schoolCommissionReport') {
    return (
      <div>
        <NavBar />
        <SchoolCommissionReport
          onClose={() => setCurrentView('reportsCategory')}
        />
      </div>
    );
  }

  // Dashboard View
  return (
    <Dashboard 
      user={user}
      onNavigate={handleNavigate}
      onLogout={handleLogout}
    />
  );
}

export default App;